import { useState } from "react";
import { FiLock } from "react-icons/fi";
import officeIcon from "../../assets/contact-us-page/Background+Border.png";
import phoneIcon from "../../assets/contact-us-page/Background+Border-1.png";
import emailIcon from "../../assets/contact-us-page/mail-icon.png";
import entityIcon from "../../assets/contact-us-page/Background+Border-2.png";
import facebookIcon from "../../assets/contact-us-page/Link.png";
import linkedinIcon from "../../assets/contact-us-page/Link-1.png";
import youtubeIcon from "../../assets/contact-us-page/Link-2.png";
import whatsappIcon from "../../assets/contact-us-page/Link-3.png";
import { siteConfig } from "../../config/site";
import { useInView } from "../../hooks/useInView";

const CONTACT_CARDS = [
  {
    icon: officeIcon,
    label: "OFFICE",
    value: siteConfig.address,
    extra: null,
  },
  {
    icon: phoneIcon,
    label: "PHONE",
    value: siteConfig.phoneDisplay,
    extra: "Mon-Sat, 10AM - 8PM",
  },
  {
    icon: emailIcon,
    label: "EMAIL SUPPORT",
    value: siteConfig.supportEmail,
    extra: "Response within 2 hours",
  },
  {
    icon: entityIcon,
    label: "REGISTERED ENTITY",
    value: siteConfig.cin ? `CIN: ${siteConfig.cin}` : "",
    extra: "✓ Verified Identity",
  },
].filter((card) => card.value);

const SOCIAL_LINKS = [
  { icon: facebookIcon, label: "Facebook", href: siteConfig.social.facebook || "#" },
  { icon: linkedinIcon, label: "LinkedIn", href: siteConfig.social.linkedin || "#" },
  { icon: youtubeIcon, label: "YouTube", href: "#" },
  { icon: whatsappIcon, label: "WhatsApp", href: siteConfig.social.whatsapp || "#" },
];

function FormField({
  label,
  placeholder,
  type = "text",
}: {
  label: string;
  placeholder: string;
  type?: string;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs font-semibold uppercase tracking-[0.4px] text-[#334155]">
        {label}
      </span>
      <input
        type={type}
        placeholder={placeholder}
        className="h-[46px] rounded-lg border border-solid border-[#e2e8f0] bg-[#f8fafc] px-[17px] text-sm text-[#1c1917] outline-none placeholder:text-[#9ca3af] focus:ring-2 focus:ring-brand-purple/30"
      />
    </label>
  );
}

export function ContactSection() {
  const [consent, setConsent] = useState(false);
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section className="mx-auto w-full max-w-[1232px] px-4 py-16 sm:py-20">
      <div
        ref={ref}
        className={`grid grid-cols-1 gap-10 lg:grid-cols-2 ${inView ? "in-view" : ""}`}
      >
        {/* Left: Form card */}
        <div className="rounded-2xl border border-solid border-[#e2e8f0] bg-white p-6 shadow-[0px_1px_3px_rgba(0,0,0,0.06)] sm:p-[41px]">
          <h2 className="reveal-scroll delay-1 text-2xl font-bold leading-tight text-brand-purple sm:text-3xl">
            Leave your message
          </h2>
          <p className="reveal-scroll delay-2 pt-1 text-sm text-[#64748b]">
            Tell us about your loan requirements and questions.
          </p>

          <form className="mt-8 flex flex-col gap-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Full Name" placeholder="Your Name" />
              <FormField
                label="Email Address"
                type="email"
                placeholder="Your Email"
              />
            </div>

            <FormField
              label="Phone Number"
              type="tel"
              placeholder="+91 98765 43210"
            />

            <label className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.4px] text-[#334155]">
                Tell us about your requirements...
              </span>
              <textarea
                placeholder="Write your loan inquiry or question here..."
                rows={4}
                className="resize-none rounded-lg border border-solid border-[#e2e8f0] bg-[#f8fafc] px-[17px] py-[13px] text-sm text-[#1c1917] outline-none placeholder:text-[#9ca3af] focus:ring-2 focus:ring-brand-purple/30"
              />
            </label>

            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-[2px] h-5 w-5 shrink-0 rounded border-2 border-[#cbd5e1] text-brand-purple focus:ring-brand-purple/30"
              />
              <span className="text-xs leading-5 text-[#64748b]">
                I consent to receive secure communications via RCS, WhatsApp,
                email, or SMS regarding my inquiry and agree to the privacy
                policy.
              </span>
            </label>

            <div className="flex flex-col items-start gap-4 pt-1 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                className="flex items-center gap-2 rounded-full bg-[#311042] px-8 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                Submit Message
                <span aria-hidden="true">→</span>
              </button>
              <span className="flex items-center gap-2 text-xs text-[#64748b]">
                <FiLock className="h-3 w-3 text-brand-teal" />
                Bank-grade 256-bit encryption
              </span>
            </div>
          </form>
        </div>

        {/* Right: Contact info */}
        <div className="flex flex-col">
          <h2 className="reveal-scroll delay-1 text-2xl font-bold leading-tight text-[#1c1917] sm:text-3xl">
            Don't hesitate to contact us
          </h2>
          <p className="reveal-scroll delay-2 pt-3 text-sm leading-6 text-[#475569]">
            With over 20 years of collective finance experience, our
            certified loan advisors deliver transparent, lightning-fast
            financial solutions without hidden costs or commitments.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CONTACT_CARDS.map((card) => (
              <div
                key={card.label}
                className="flex items-start gap-4 rounded-2xl border border-solid border-[#e2e8f0] p-5"
              >
                <img src={card.icon} alt="" className="h-11 w-11 shrink-0" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.4px] text-[#64748b]">
                    {card.label}
                  </p>
                  <p className="pt-1 text-sm font-bold leading-5 text-[#1c1917]">
                    {card.value}
                  </p>
                  {card.extra ? (
                    <p className="pt-1 text-xs text-[#64748b]">{card.extra}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <span className="text-sm font-medium text-[#334155]">
              Social Media :
            </span>
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                aria-label={`Follow us on ${social.label}`}
                className="h-9 w-9 shrink-0 transition-opacity hover:opacity-80"
              >
                <img src={social.icon} alt="" className="h-9 w-9" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
